/**
 * ConfirmationPopupInterface - Configuration interface for confirmation popup dialogs
 *
 * Following SOLID Principles:
 * - Interface Segregation: Provides optional properties for flexible popup configurations
 * - Single Responsibility: Defines only popup appearance and behavior options
 *
 * Used by DialogService to create various types of confirmation popups
 * Supports customization of title, message, buttons, icons, images, and dimensions
 */
export interface ConfirmationPopupInterface {
    title: string;
    titleClass?: string;
    message?: string;
    cancelText?: string;
    confirmText?: string;
    isShowAaeInfo?: boolean;
    icon?: string;
    imagePath?: string; // Path to image file (e.g., SVG, PNG) for illustration-style popups
    imageWidth?: string; // Custom image width (e.g., '212px', '300px', '100%') - defaults to '300px'
    imageHeight?: string; // Custom image height (e.g., '190px', 'auto', '200px') - defaults to 'auto'
    isDelete?: boolean;
    isResetPasswordAwareness?: boolean;
    customWidth?: string; // Custom width for dialog (e.g., '550px', '80vw')
    customHeight?: string; // Custom height for dialog (e.g., '440px', 'auto')
    cancelButtonClass?: string; // Custom CSS classes for cancel button
    confirmButtonClass?: string; // Custom CSS classes for confirm button
    /**
     * Controls whether users can close the dialog by clicking outside or pressing ESC
     * - true: Dialog cannot be closed by clicking backdrop or pressing ESC (requires button click)
     * - false: Dialog can be closed by clicking outside or pressing ESC
     * @default true
     */
    disableClose?: boolean;
}

/**
 * ConfirmationPopupModel - Model class for confirmation popup dialogs
 *
 * Following SOLID Principles:
 * - Single Responsibility: Only handles popup configuration data with default values
 * - Dependency Inversion: Depends on ConfirmationPopupInterface abstraction
 *
 * Provides default values for all optional properties from ConfirmationPopupInterface
 * Used by ConfirmationPopupComponent to render dialog content
 */
export class ConfirmationPopupModel {
    title: string;
    titleClass?: string;
    message: string;
    cancelText: string;
    confirmText: string;
    isShowAaeInfo: boolean;
    icon: string;
    imagePath: string;
    imageWidth: string;
    imageHeight: string;
    isDelete: boolean;
    isResetPasswordAwareness: boolean;
    customWidth: string;
    customHeight: string;
    cancelButtonClass: string;
    confirmButtonClass: string;
    disableClose: boolean;

    /**
     * Creates a ConfirmationPopupModel with default values for optional properties
     *
     * Default values:
     * - confirmText: 'button.confirm'
     * - imageWidth: '300px'
     * - imageHeight: 'auto'
     * - disableClose: true
     * - All boolean flags: false
     * - All optional strings: empty string
     *
     * @param model - The interface containing popup configuration
     */
    constructor(model: ConfirmationPopupInterface) {
        this.title = model.title || '';
        this.message = model.message || '';
        this.titleClass = model.titleClass || '';
        this.cancelText = model.cancelText || '';
        this.confirmText = model.confirmText || 'button.confirm';
        this.isShowAaeInfo = model.isShowAaeInfo || false;
        this.icon = model.icon || '';
        this.imagePath = model.imagePath || '';
        this.imageWidth = model.imageWidth || '300px'; // Default to current max-width behavior
        this.imageHeight = model.imageHeight || 'auto'; // Default to current height behavior
        this.isDelete = model.isDelete || false;
        this.isResetPasswordAwareness = model.isResetPasswordAwareness || false;
        this.customWidth = model.customWidth || '';
        this.customHeight = model.customHeight || '';
        this.cancelButtonClass = model.cancelButtonClass || '';
        this.confirmButtonClass = model.confirmButtonClass || '';
        // Default to true: prevent accidental dismissal of confirmation dialogs
        this.disableClose = model.disableClose !== undefined ? model.disableClose : true;
    }
}
